import {
  React,
  useState,
  useEffect,
  useAuth,
  useClientInfo,
  axiosInstance,
  convertDDMMYYYYtoISO,
  locationData,
  EmployeeBankSection,
  DocumentManager,
  CategoryDropdown,
  Box,
  Button,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Typography,
  SnackbarAlert,
} from "../../utils/commonImports";

import EmployeePhoto from "./EmployeePhoto";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

/* ------------------------------------------------------------------ */
/* Constants                                                           */
/* ------------------------------------------------------------------ */
const salutationOptions = ["Mr.", "Ms.", "Mrs.", "Dr."];
const employeeTypeOptions = ["Staff", "Driver"];
const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-"];
const PAYMENT_MODES = ["Account", "Cash"];
const TABS = ["Address", "Personal", "Payroll", "Bank", "Documents"];

const API = "/api/employees";

const emptyAddress = {
  country: "",
  address1: "",
  address2: "",
  city: "",
  state: "",
  pincode: "",
};

const initialFormState = {
  salutation: "",
  first_name: "",
  last_name: "",
  designation: "",
  email: "",
  phone: "",
  driver_type: "",
  photo: null,
  photofilename: "",
  employeeid: "",
  client_id: "",
  present_address: { ...emptyAddress },
  permanent_address: { ...emptyAddress },
  permanent_same_as_present: false,
  dob: "",
  blood_group: "",
  father_name: "",
  experience1: "",
  reference1: "",
  reference2: "",
  emergency_contact: "",
  contact1: "",
  contact2: "",
  doj: "",
  access_id: "",
  basic: "",
  hra: "",
  da: "",
  conveyance: "",
  allowance: "",
  sa: "",
  pf_number: "",
  esi_number: "",
  pf_per: "",
  cpf_per: "",
  esi_per: "",
  cesi_per: "",
  bonus_per: "",
  pt_amount: "",
  tds_per: "",
  mode_of_payment: "",
  left_type: "",
  dol: "",
  reason: "",
  bank_name: "",
  bank_account_type: "",
  bank_account_number: "",
  bank_branch: "",
  ifsc_code: "",
  document_name: "",
  document_number: "",
  document_expiry_date: "",
  document_file: null,
};

const initialBankInput = {
  EmployeeBankName: "",
  EmployeeAccountType: "",
  EmployeeAccountnumber: "",
  EmployeeBranch: "",
  EmployeeBankIFSC: "",
  BankActive: "Yes",
};

// Field groups for the Personal / Payroll tabs: [label, name]
const personalFields = [
  ["Father's name", "father_name"],
  ["Experience", "experience1"],
];
const contactFields = [
  ["Emergency contact", "emergency_contact"],
  ["Contact 1", "contact1"],
  ["Contact 2", "contact2"],
  ["Reference 1", "reference1"],
  ["Reference 2", "reference2"],
];
const salaryFields = [
  ["Basic salary", "basic"],
  ["HRA", "hra"],
  ["DA", "da"],
  ["Conveyance", "conveyance"],
  ["Allowance", "allowance"],
  ["SA", "sa"],
];
const statutoryFields = [
  ["PF number", "pf_number"],
  ["ESI number", "esi_number"],
  ["PF %", "pf_per"],
  ["CPF %", "cpf_per"],
  ["ESI %", "esi_per"],
  ["CESI %", "cesi_per"],
  ["Bonus %", "bonus_per"],
  ["PT amount", "pt_amount"],
  ["TDS %", "tds_per"],
];

/* ------------------------------------------------------------------ */
/* Compact design tokens                                               */
/* ------------------------------------------------------------------ */
const C = {
  primary: "#1d4ed8",
  primaryHover: "#1e40af",
  border: "#e2e8f0",
  surface: "#f8fafc",
  text: "#0f172a",
  muted: "#64748b",
};

const fieldSx = {
  "& .MuiInputBase-root": { fontSize: 13, backgroundColor: "#fff" },
  "& .MuiInputBase-input": { py: "7px" },
  "& .MuiSelect-select": { py: "7px !important" },
  "& .MuiInputLabel-root": { fontSize: 13 },
  "& .MuiInputLabel-shrink": { fontSize: 13 },
};

const btnBase = {
  textTransform: "none",
  fontSize: 13,
  fontWeight: 500,
  borderRadius: "6px",
  boxShadow: "none",
  minWidth: 0,
  px: 2,
  py: 0.6,
};

/* ------------------------------------------------------------------ */
/* Small presentational helpers                                        */
/* ------------------------------------------------------------------ */
const Card = ({ title, action, children, sx }) => (
  <Box
    sx={{
      border: `1px solid ${C.border}`,
      borderRadius: "8px",
      backgroundColor: "#fff",
      ...sx,
    }}
  >
    {(title || action) && (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1,
          minHeight: 40,
          borderBottom: `1px solid ${C.border}`,
          backgroundColor: C.surface,
          borderRadius: "8px 8px 0 0",
        }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.text }}>
          {title}
        </Typography>
        {action}
      </Box>
    )}
    <Box sx={{ p: 2 }}>{children}</Box>
  </Box>
);

const Grid = ({ cols = 3, children, sx }) => (
  <Box
    sx={{
      display: "grid",
      gap: 1.5,
      gridTemplateColumns: {
        xs: "1fr",
        sm: "repeat(2, 1fr)",
        md: `repeat(${cols}, 1fr)`,
      },
      alignItems: "start",
      ...sx,
    }}
  >
    {children}
  </Box>
);

// Text input / select with consistent dense styling
const Field = ({ options, sx, ...props }) => (
  <TextField
    size="small"
    fullWidth
    select={!!options}
    sx={{ ...fieldSx, ...sx }}
    {...props}
  >
    {options &&
      options.map((o) => {
        const opt = typeof o === "string" ? { value: o, label: o } : o;
        return (
          <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 13 }}>
            {opt.label}
          </MenuItem>
        );
      })}
  </TextField>
);

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */
function EmployeeFormAdd({ editData, onCancel, onSuccess }) {
  const { clientId } = useClientInfo();
  const { token } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("Address");
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(initialFormState);
  const [banks, setBanks] = useState([]);
  const [bankInput, setBankInput] = useState(initialBankInput);

  const [documents, setDocuments] = useState([]);
  const [deletedDocumentIds, setDeletedDocumentIds] = useState([]);
  const [employeeId, setEmployeeId] = useState(editData?.employeeid || null);

  const handleDocumentsChange = (docs, deletedIds) => {
    setDocuments(docs);
    setDeletedDocumentIds(deletedIds || []);
  };

  /* ---------------------------- Effects ---------------------------- */
  // Auto-set client_id
  useEffect(() => {
    if (clientId && !form.client_id) {
      setForm((prev) => ({ ...prev, client_id: clientId }));
    }
  }, [clientId, form.client_id]);

  // Populate edit form fully
  useEffect(() => {
    if (editData?.employeeid) {
      setForm({
        ...initialFormState,
        employeeid: editData.employeeid,
        client_id: clientId || editData.client_id || "",
        salutation: editData.employeesalutation || "",
        first_name: editData.employeefirstname || "",
        last_name: editData.employeelastname || "",
        designation: editData.employeedesignation || "",
        email: editData.employeeemailaddress || "",
        phone: editData.employeemobile || "",
        driver_type: editData.employeedrivertype || "", // Employee type
        dob: editData.employeedob
          ? convertDDMMYYYYtoISO(editData.employeedob)
          : "",
        blood_group: editData.employeebloodgroup || "",
        father_name: editData.employeefathername || "",
        experience1: editData.employeeexperience1 || "",
        reference1: editData.employeereference1 || "",
        reference2: editData.employeereference2 || "",
        emergency_contact: editData.employeeemergencycontact || "",
        contact1: editData.employeecontact1 || "",
        contact2: editData.employeecontact2 || "",
        doj: editData.employeedoj
          ? convertDDMMYYYYtoISO(editData.employeedoj)
          : "",
        access_id: editData.employeeaccessid || "",
        basic: editData.employeebasic ?? "",
        hra: editData.employeehra ?? "",
        da: editData.employeeda ?? "",
        conveyance: editData.employeeconveyance ?? "",
        allowance: editData.employeeallowance ?? "",
        sa: editData.employeesa ?? "",
        pf_number: editData.employeepfnumber || "",
        esi_number: editData.employeeesinumber || "",
        pf_per: editData.emplyeepfper ?? "",
        cpf_per: editData.employeecpfper ?? "",
        esi_per: editData.employeeesiper ?? "",
        cesi_per: editData.employeecesiper ?? "",
        bonus_per: editData.employeebonusper ?? "",
        pt_amount: editData.employeeptamount ?? "",
        tds_per: editData.employeetdsper ?? "",
        mode_of_payment: editData.employeemodeofpayment || "",
        left_type: editData.employeelefttype || "",
        dol: editData.employeedol
          ? convertDDMMYYYYtoISO(editData.employeedol)
          : "",
        reason: editData.employeereason || "",
        present_address: {
          country: editData.employeepresentcountry || "",
          address1: editData.employeepresentstreet1 || "",
          address2: editData.employeepresentstreet2 || "",
          city: editData.employeepresentcity || "",
          state: editData.employeepresentstate || "",
          pincode: editData.employeepresentpincode || "",
        },
        permanent_address: {
          country: editData.employeepermanentcountry || "",
          address1: editData.employeepermanentstreet1 || "",
          address2: editData.employeepermanentstreet2 || "",
          city: editData.employeepermanentcity || "",
          state: editData.employeepermanentstate || "",
          pincode: editData.employeepermanentpincode || "",
        },
        photofilename: editData.photofilename || "",
        employeephotopath: editData.employeephotopath || "",
      });
      fetchBankDetails(editData.employeeid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editData, clientId]);

  /* ------------------------------ Banks ---------------------------- */
  const fetchBankDetails = async (employeeId) => {
    try {
      const response = await axiosInstance.get(
        `/api/employees/${employeeId}/banks`
      );
      const formattedBanks = (response.data || []).map((bank) => ({
        client_id: clientId,
        bankid: bank.bankid,
        EmployeeBankName: bank.employeebankname,
        EmployeeAccountType: bank.employeeaccounttype,
        EmployeeAccountnumber: bank.employeeaccountnumber,
        EmployeeBranch: bank.employeebranch,
        EmployeeBankIFSC: bank.employeebankifsc,
        BankActive: bank.bankactive,
      }));
      setBanks(formattedBanks);
    } catch (err) {
      console.error("Error fetching bank details:", err.message);
      setBanks([]);
    }
  };

  const handleBankChange = (e) => {
    const { name, value } = e.target;
    setBankInput({ ...bankInput, [name]: value });
  };

  const addBank = () => {
    if (!bankInput.EmployeeBankName || !bankInput.EmployeeAccountnumber) {
      SnackbarAlert("Bank Name and Account Number are required", "error");
      return;
    }
    setBanks([...banks, { ...bankInput }]);
    setBankInput(initialBankInput);
  };

  const editBank = (index) => {
    const selectedBank = banks[index];
    setBankInput(selectedBank);
    const remainingBanks = [...banks];
    remainingBanks.splice(index, 1);
    setBanks(remainingBanks);
  };

  const deleteBank = (index) => {
    const updatedBanks = [...banks];
    updatedBanks.splice(index, 1);
    setBanks(updatedBanks);
  };

  /* ---------------------------- Handlers --------------------------- */
  // Photo change for EmployeePhoto
  const handlePhotoChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setForm((prev) => ({
        ...prev,
        photo: e.target.files[0], // store File object
      }));
    }
  };

  // Flat change handler
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // Update part of present / permanent address
  const updateAddress = (addressKey, patch) =>
    setForm((prev) => ({
      ...prev,
      [addressKey]: { ...prev[addressKey], ...patch },
    }));

  const handleSameAsPresent = (checked) => {
    if (checked) {
      setForm((prev) => ({
        ...prev,
        permanent_same_as_present: true,
        permanent_address: { ...prev.present_address },
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        permanent_same_as_present: false,
        permanent_address: { ...emptyAddress },
      }));
    }
  };

  /* ------------------------------ Save ----------------------------- */
  const saveEmployee = async () => {
    if (!clientId) throw new Error("Client ID missing");
    const isEditMode = !!editData?.employeeid;

    function normalizeNumber(value) {
      if (value === "" || value === null || value === undefined) return null;
      return Number(value);
    }

    const payload = {
      client_id: form.client_id,
      employeesalutation: form.salutation,
      employeefirstname: form.first_name,
      employeelastname: form.last_name,
      employeeemailaddress: form.email,
      employeemobile: form.phone,
      employeedesignation: form.designation,
      employeedrivertype: form.driver_type,
      photofilename: form.photofilename || "",
      employeephotopath: form.employeephotopath || "",
      employeedob: convertDDMMYYYYtoISO(form.dob) || null,
      employeebloodgroup: form.blood_group,
      employeefathername: form.father_name,
      employeeexperience1: form.experience1,
      employeereference1: form.reference1,
      employeereference2: form.reference2,
      employeeemergencycontact: form.emergency_contact,
      employeecontact1: form.contact1,
      employeecontact2: form.contact2,
      employeedoj: convertDDMMYYYYtoISO(form.doj) || null,
      employeeaccessid: form.access_id,
      employeebasic: normalizeNumber(form.basic),
      employeehra: normalizeNumber(form.hra),
      employeeda: normalizeNumber(form.da),
      employeeconveyance: normalizeNumber(form.conveyance),
      employeeallowance: normalizeNumber(form.allowance),
      employeesa: normalizeNumber(form.sa),
      employeepfnumber: form.pf_number,
      employeeesinumber: form.esi_number,
      emplyeepfper: normalizeNumber(form.pf_per),
      employeecpfper: normalizeNumber(form.cpf_per),
      employeeesiper: normalizeNumber(form.esi_per),
      employeecesiper: normalizeNumber(form.cesi_per),
      employeebonusper: normalizeNumber(form.bonus_per),
      employeeptamount: normalizeNumber(form.pt_amount),
      employeetdsper: normalizeNumber(form.tds_per),
      employeemodeofpayment: form.mode_of_payment,
      employeelefttype: form.left_type,
      employeedol: convertDDMMYYYYtoISO(form.dol) || null,
      employeereason: form.reason,
      updated_by: "system",
      employeepresentcountry: form.present_address.country,
      employeepresentstreet1: form.present_address.address1,
      employeepresentstreet2: form.present_address.address2,
      employeepresentcity: form.present_address.city,
      employeepresentstate: form.present_address.state,
      employeepresentpincode: form.present_address.pincode,
      employeepermanentcountry: form.permanent_address.country,
      employeepermanentstreet1: form.permanent_address.address1,
      employeepermanentstreet2: form.permanent_address.address2,
      employeepermanentcity: form.permanent_address.city,
      employeepermanentstate: form.permanent_address.state,
      employeepermanentpincode: form.permanent_address.pincode,
    };

    const formData = new FormData();

    if (form.photo instanceof File) {
      formData.append("employeephoto", form.photo); // match multer field name
      formData.append("photofilename", form.photo.name);
    } else {
      formData.append("photofilename", form.photofilename || "");
    }

    Object.entries(payload).forEach(([key, value]) => {
      if (key !== "photofilename") {
        formData.append(key, value == null ? "" : value);
      }
    });

    try {
      let response;
      if (isEditMode) {
        response = await axiosInstance.put(
          `${API}/${editData.employeeid}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        return editData.employeeid;
      } else {
        response = await axiosInstance.post(API, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        });
        return (
          response.data.employeeId ||
          response.data.employeeid ||
          response.data.id
        );
      }
    } catch (err) {
      console.error(
        "Failed to save employee:",
        err.response?.data || err.message
      );
      return null;
    }
  };

  const saveBanks = async (employeeId) => {
    const validBanks = banks.filter(
      (b) => b.EmployeeBankName && b.EmployeeAccountnumber
    );
    if (validBanks.length === 0) return;
    const formattedBanks = validBanks.map((b) => ({
      client_id: clientId,
      employeeid: employeeId,
      employeebankname: b.EmployeeBankName,
      employeeaccounttype: b.EmployeeAccountType,
      employeeaccountnumber: b.EmployeeAccountnumber,
      employeebranch: b.EmployeeBranch,
      employeebankifsc: b.EmployeeBankIFSC,
      bankactive: b.BankActive || "Yes",
      created_by: "admin",
      updated_by: "admin",
    }));
    await axiosInstance.post(
      `/api/employees/${employeeId}/banks`,
      formattedBanks
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!clientId) {
      console.error("clientId is undefined!");
      SnackbarAlert("Client not selected. Cannot save employee.", "error");
      return;
    }

    setSaving(true);
    try {
      // 1. Save employee
      const newEmployeeId = await saveEmployee();
      if (!newEmployeeId) throw new Error("Failed to save employee");

      setEmployeeId(newEmployeeId);

      // 2. Upload photo separately if a new file is selected
      if (form.photo instanceof File) {
        const photoForm = new FormData();
        photoForm.append("photoFile", form.photo); // same field name as controller
        photoForm.append("photofilename", form.photo.name);

        try {
          const photoRes = await axiosInstance.put(
            `/api/employees/${newEmployeeId}/photo`,
            photoForm,
            { headers: { "Content-Type": "multipart/form-data" } }
          );

          // Update form state with returned photo info
          setForm((prev) => ({
            ...prev,
            photofilename: photoRes.data.photofilename,
            employeephotopath: photoRes.data.employeephotopath,
          }));
        } catch (photoErr) {
          console.error(
            "Failed to upload employee photo:",
            photoErr.response?.data || photoErr.message
          );
          SnackbarAlert("Failed to upload employee photo.", "error");
        }
      }

      // 3. Upload documents
      const formData = new FormData();
      formData.append("documentsMeta", JSON.stringify(documents));
      documents.forEach((doc) => {
        if (doc.file instanceof File)
          formData.append("documentFiles", doc.file);
      });
      formData.append("client_id", clientId);

      await axiosInstance.post(
        `/api/documents/employee/${newEmployeeId}/batch-save/${clientId}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      // 4. Save bank details
      await saveBanks(newEmployeeId);

      SnackbarAlert("Employee saved successfully!", "success");
      toast.success("Employee added successfully ✅");

      // Reset documents and deleted IDs
      setDocuments([]);
      setDeletedDocumentIds([]);

      navigate("/employees");
      onSuccess?.();
       } catch (err) {
      const responseData = err.response?.data;

      console.error(
        "Error saving employee and documents:",
        responseData || err.message
      );

      const message =
        responseData?.message ||
        responseData?.error ||
        err.message ||
        "Failed to save employee and documents.";

      SnackbarAlert(message, "error");
    } finally {
      setSaving(false);
    }
  };

  /* ------------------------------ Tabs ----------------------------- */
  const tabCount = { Bank: banks.length, Documents: documents.length };

  // Plain text field bound to form[name] (render function, not a component)
  const textField = ([label, name], extra = {}) => (
    <Field
      key={name}
      label={label}
      name={name}
      value={form[name] || ""}
      onChange={handleChange}
      {...extra}
    />
  );

  const renderAddressCard = (section) => {
    const isPermanent = section === "Permanent";
    const addressKey = isPermanent ? "permanent_address" : "present_address";
    const address = form[addressKey] || {};
    const locked = isPermanent && form.permanent_same_as_present;

    const countries = locationData ? Object.keys(locationData) : [];
    const states =
      address.country && locationData[address.country]
        ? Object.keys(locationData[address.country])
        : [];
    const cities =
      address.country &&
      address.state &&
      locationData[address.country] &&
      locationData[address.country][address.state]
        ? locationData[address.country][address.state]
        : [];

    const withEmpty = (list, label) => [
      { value: "", label },
      ...list.map((v) => ({ value: v, label: v })),
    ];

    return (
      <Card
        key={section}
        title={`${section} address`}
        action={
          isPermanent && (
            <FormControlLabel
              sx={{ m: 0, "& .MuiFormControlLabel-label": { fontSize: 12 } }}
              control={
                <Checkbox
                  size="small"
                  sx={{ p: 0.5 }}
                  checked={form.permanent_same_as_present || false}
                  onChange={(e) => handleSameAsPresent(e.target.checked)}
                />
              }
              label="Same as present"
            />
          )
        }
      >
        <Grid cols={2}>
          <Field
            label="Country/Region"
            value={address.country || ""}
            disabled={locked}
            onChange={(e) =>
              updateAddress(addressKey, {
                country: e.target.value,
                state: "",
                city: "",
              })
            }
            options={withEmpty(countries, "Select country")}
          />
          <Field
            label="State"
            value={address.state || ""}
            disabled={locked || !address.country}
            onChange={(e) =>
              updateAddress(addressKey, { state: e.target.value, city: "" })
            }
            options={withEmpty(states, "Select state")}
          />
          <Field
            label="City"
            value={address.city || ""}
            disabled={locked || !address.state}
            onChange={(e) => updateAddress(addressKey, { city: e.target.value })}
            options={withEmpty(cities, "Select city")}
          />
          <Field
            label="Pincode"
            value={address.pincode || ""}
            disabled={locked}
            onChange={(e) =>
              updateAddress(addressKey, { pincode: e.target.value })
            }
          />
          <Field
            label="Street 1"
            value={address.address1 || ""}
            disabled={locked}
            onChange={(e) =>
              updateAddress(addressKey, { address1: e.target.value })
            }
            sx={{ gridColumn: { sm: "span 2" } }}
          />
          <Field
            label="Street 2"
            value={address.address2 || ""}
            disabled={locked}
            onChange={(e) =>
              updateAddress(addressKey, { address2: e.target.value })
            }
            sx={{ gridColumn: { sm: "span 2" } }}
          />
        </Grid>
      </Card>
    );
  };

  const renderFields = () => {
    switch (activeTab) {
      case "Address":
        return (
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              alignItems: "start",
            }}
          >
            {["Present", "Permanent"].map(renderAddressCard)}
          </Box>
        );

      case "Personal":
        return (
          <Box sx={{ display: "grid", gap: 2 }}>
            <Card title="Personal details">
              <Grid cols={4}>
                <Field
                  type="date"
                  label="Date of birth"
                  name="dob"
                  value={form.dob || ""}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
                <Field
                  label="Blood group"
                  name="blood_group"
                  value={form.blood_group || ""}
                  onChange={handleChange}
                  options={[
                    { value: "", label: "Select" },
                    ...BLOOD_GROUPS.map((g) => ({ value: g, label: g })),
                  ]}
                />
                {personalFields.map((f) => textField(f))}
              </Grid>
            </Card>
            <Card title="Contacts & references">
              <Grid cols={4}>{contactFields.map((f) => textField(f))}</Grid>
            </Card>
          </Box>
        );

      case "Payroll":
        return (
          <Box sx={{ display: "grid", gap: 2 }}>
            <Card title="Employment">
              <Grid cols={4}>
                <Field
                  type="date"
                  label="Date of joining"
                  name="doj"
                  value={form.doj || ""}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
                {textField(["Access ID", "access_id"])}
                <Field
                  label="Mode of payment"
                  name="mode_of_payment"
                  value={form.mode_of_payment || ""}
                  onChange={handleChange}
                  options={[
                    { value: "", label: "Select" },
                    ...PAYMENT_MODES.map((m) => ({ value: m, label: m })),
                  ]}
                />
                <Field
                  label="Employee status"
                  name="left_type"
                  value={form.left_type || ""}
                  onChange={handleChange}
                  options={[
                    { value: "", label: "Select status" },
                    "Active",
                    "Inactive",
                  ]}
                />
                <Field
                  type="date"
                  label="Date of leaving"
                  name="dol"
                  value={form.dol || ""}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
                {textField(["Reason", "reason"], {
                  sx: { gridColumn: { md: "span 3" } },
                })}
              </Grid>
            </Card>
            <Card title="Salary">
              <Grid cols={6}>{salaryFields.map((f) => textField(f))}</Grid>
            </Card>
            <Card title="PF, ESI & deductions">
              <Grid cols={5}>{statutoryFields.map((f) => textField(f))}</Grid>
            </Card>
          </Box>
        );

      case "Bank":
        return (
          <EmployeeBankSection
            bankInput={bankInput}
            banks={banks}
            handleBankChange={handleBankChange}
            addBank={addBank}
            editBank={editBank}
            deleteBank={deleteBank}
          />
        );

      case "Documents":
        return (
          <DocumentManager
            entityType="employee"
            entityId={employeeId} // set after the first save
            clientId={clientId}
            onDocumentsChange={handleDocumentsChange}
            documentCategoryType="EmployeeDocument"
          />
        );

      default:
        return null;
    }
  };

  /* ------------------------------ UI ------------------------------- */
  return (
    <form onSubmit={handleSubmit} autoComplete="off">
      <Box
        sx={{
          mx: "auto",
          my: 1,
          maxWidth: 1400,
          bgcolor: "#fff",
          borderRadius: "10px",
          border: `1px solid ${C.border}`,
          minHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* ---------- Header ---------- */}
        <Box
          sx={{
            px: 3,
            py: 1.5,
            borderBottom: `1px solid ${C.border}`,
            backgroundColor: C.surface,
          }}
        >
          <Typography sx={{ fontSize: 17, fontWeight: 700, color: C.text }}>
            {editData ? "Edit employee" : "New employee"}
          </Typography>
        </Box>

        <Box sx={{ p: 3, flex: 1 }}>
          {/* ---------- Basic details + photo ---------- */}
          <Card title="Basic details">
            <Box
              sx={{
                display: "flex",
                gap: 3,
                flexWrap: { xs: "wrap", md: "nowrap" },
                alignItems: "flex-start",
              }}
            >
              <Grid cols={3} sx={{ flex: 1, minWidth: 0, width: "100%" }}>
                <Field
                  label="Salutation"
                  name="salutation"
                  value={form.salutation}
                  onChange={handleChange}
                  options={salutationOptions}
                />
                <Field
                  label="First name"
                  name="first_name"
                  value={form.first_name}
                  onChange={handleChange}
                />
                <Field
                  label="Last name"
                  name="last_name"
                  value={form.last_name}
                  onChange={handleChange}
                />
                <Field
                  label="Email ID"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                />
                <Field
                  label="Phone no."
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                />
                <CategoryDropdown
                  categoryType="Designation"
                  name="designation"
                  size="small"
                  value={form.designation}
                  onChange={handleChange}
                  label="Designation"
                />
                <Field
                  label="Employee type"
                  name="driver_type"
                  value={form.driver_type}
                  onChange={handleChange}
                  options={employeeTypeOptions}
                />
              </Grid>

              <Box sx={{ width: { xs: "100%", md: 200 }, flexShrink: 0 }}>
                <EmployeePhoto
                  form={form}
                  handlePhotoChange={handlePhotoChange}
                />
                <input
                  type="file"
                  name="photo"
                  accept="image/*"
                  onChange={(e) =>
                    setForm({ ...form, photo: e.target.files[0] })
                  }
                  style={{ marginTop: 8, width: "100%", fontSize: 12 }}
                />
              </Box>
            </Box>
          </Card>

          {/* ---------- Tab bar ---------- */}
          <Box
            sx={{
              display: "flex",
              gap: 0.5,
              mt: 3,
              mb: 2,
              borderBottom: `1px solid ${C.border}`,
              overflowX: "auto",
            }}
          >
            {TABS.map((tab) => {
              const active = activeTab === tab;
              const count = tabCount[tab];
              return (
                <button
                  type="button"
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  style={{
                    padding: "8px 14px",
                    fontSize: 13,
                    fontWeight: active ? 600 : 500,
                    color: active ? C.primary : C.muted,
                    background: "none",
                    border: "none",
                    borderBottom: `2px solid ${
                      active ? C.primary : "transparent"
                    }`,
                    marginBottom: -1,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  {tab}
                  {count > 0 && (
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        lineHeight: 1,
                        padding: "3px 6px",
                        borderRadius: 10,
                        color: active ? C.primary : C.muted,
                        background: active ? "#dbeafe" : "#f1f5f9",
                      }}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </Box>

          {/* ---------- Tab content ---------- */}
          {renderFields()}
        </Box>

        {/* ---------- Sticky action bar ---------- */}
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            zIndex: 2,
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            px: 3,
            py: 1.5,
            bgcolor: "#fff",
            borderTop: `1px solid ${C.border}`,
          }}
        >
          <Button
            variant="outlined"
            color="inherit"
            onClick={onCancel}
            disabled={saving}
            sx={{ ...btnBase, borderColor: C.border, color: C.muted }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={saving}
            sx={{
              ...btnBase,
              backgroundColor: C.primary,
              "&:hover": { backgroundColor: C.primaryHover },
            }}
          >
            {saving
              ? "Saving…"
              : editData
              ? "Update employee"
              : "Save employee"}
          </Button>
        </Box>
      </Box>
    </form>
  );
}

export default EmployeeFormAdd;
